/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import dayjs from 'dayjs';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type {
  CalendarEventInput,
  EventRecurrenceScope,
} from '@services/requests/housien-models/CalendarEventInput';
import type {
  CalendarInput,
  CalendarUpdateInput,
} from '@services/requests/housien-models/CalendarInput';
import type { UserRole } from '@/shared/utils/permissions';
import { EventDrawer } from '@features/agenda/components/EventDrawer';
import { EventPreviewDialog } from '@features/agenda/components/EventPreviewDialog';
import { RecurrenceScopeDialog } from '@features/agenda/components/RecurrenceScopeDialog';
import { CalendarSettingsDrawer } from '@features/agenda/components/CalendarSettingsDrawer';
import { writableCalendars } from '@features/agenda/utils/calendar-write-access';

export type AgendaOverlaysProps = {
  agencyId: string;
  agencyName?: string;
  userEmail: string;
  userRole: UserRole;
  calendars: Calendar[];
  calendarsLoading: boolean;
  selected: ExpandedEvent | null;
  canManageSelected: boolean;
  previewOpen: boolean;
  formOpen: boolean;
  drawerOpen: boolean;
  formDefaultDate: Date;
  pendingEdit: CalendarEventInput | null;
  pendingDelete: ExpandedEvent | null;
  onClosePreview: () => void;
  onEditFromPreview: (event: ExpandedEvent) => void;
  onRequestDelete: (event: ExpandedEvent) => void;
  onViewVisit: (event: ExpandedEvent) => void;
  onCloseForm: () => void;
  onCreateEvent: (input: CalendarEventInput) => Promise<void>;
  onUpdateEvent: (eventId: string, input: CalendarEventInput) => Promise<void>;
  onConfirmEditScope: (scope: EventRecurrenceScope) => void;
  onConfirmDeleteScope: (scope: EventRecurrenceScope) => void;
  onClearPendingEdit: () => void;
  onClearPendingDelete: () => void;
  onCreateCalendar: (input: CalendarInput) => void;
  onUpdateCalendar: (calendarId: string, body: CalendarUpdateInput) => void;
  isSavingCalendar?: boolean;
  onDeleteCalendar: (calendarId: string) => void;
  onCloseDrawer: () => void;
};

/**
 * All modal/drawer overlays for the agenda page: event preview, event drawer,
 * recurrence scope dialogs, and the calendar settings drawer.
 */
/**
 * Calendars the settings dialog may list.
 *
 * A privileged user receives their teammates' personal calendars, and the
 * dialog was listing them all under "Suas agendas" with an edit pencil — one
 * member could rename or recolour another's agenda from there. Only what the
 * user may actually manage belongs in this dialog.
 * @param props - The overlay props carrying calendars and identity.
 * @returns The manageable subset.
 */
const manageableCalendars = (props: AgendaOverlaysProps): Calendar[] =>
  writableCalendars(props.calendars, props.userEmail, props.userRole);

/**
 * AgendaOverlays component.
 */
export const AgendaOverlays = (props: AgendaOverlaysProps) => (
  <>
    {props.previewOpen && props.selected && (
      <EventPreviewDialog
        open={props.previewOpen}
        event={props.selected}
        calendars={props.calendars}
        canEdit={props.canManageSelected}
        onEdit={props.onEditFromPreview}
        onDelete={props.onRequestDelete}
        onViewVisit={props.onViewVisit}
        onClose={props.onClosePreview}
      />
    )}
    {props.formOpen && (
      <EventDrawer
        open={props.formOpen}
        agencyId={props.agencyId}
        agencyName={props.agencyName}
        calendars={writableCalendars(props.calendars, props.userEmail, props.userRole)
          .filter((c): c is Calendar & { calendar_id: string } => Boolean(c.calendar_id))
          .filter((c) => !c.sync?.google || c.calendar_id === props.selected?.calendar_id)
          .map((c) => ({
            calendar_id: c.calendar_id,
            name: c.name ?? '',
            color: c.color ?? undefined,
            is_primary: c.is_primary,
          }))}
        editingEvent={props.selected}
        initialDate={dayjs(props.formDefaultDate).format('YYYY-MM-DD')}
        initialTime={dayjs(props.formDefaultDate).format('HH:mm')}
        onSubmitCreate={props.onCreateEvent}
        onSubmitUpdate={props.onUpdateEvent}
        onClose={props.onCloseForm}
      />
    )}
    {props.pendingEdit !== null && (
      <RecurrenceScopeDialog
        open={props.pendingEdit !== null}
        action="edit"
        onConfirm={props.onConfirmEditScope}
        onClose={props.onClearPendingEdit}
      />
    )}
    {props.pendingDelete !== null && (
      <RecurrenceScopeDialog
        open={props.pendingDelete !== null}
        action="delete"
        onConfirm={props.onConfirmDeleteScope}
        onClose={props.onClearPendingDelete}
      />
    )}
    <CalendarSettingsDrawer
      open={props.drawerOpen}
      agencyId={props.agencyId}
      currentUserEmail={props.userEmail}
      userRole={props.userRole}
      calendars={manageableCalendars(props)}
      agencyName={props.agencyName}
      loading={props.calendarsLoading}
      onCreate={props.onCreateCalendar}
      onUpdate={props.onUpdateCalendar}
      isSaving={props.isSavingCalendar}
      onDelete={props.onDeleteCalendar}
      onClose={props.onCloseDrawer}
    />
  </>
);
